import { useEffect, useState } from "react";

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { DashboardFilterType } from "@/types/dashboard";

import { getDashboardProjectFilterToString } from "@/consts/Dashboard";

import { formatToPercentString, formatKoreanCurrencyWithWon } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";
import { downloadExcel, getExcelFileName } from "@/utils/excelUtils";

import { GetTopBuyersByScenario } from "@/services/Dashboard";

import DashboardCardHeader from "@/components/DashboardCardHeader";

const COLORS = ["#6665DD", "#0496FF", "#FF715B", "#DE0D92", "#34D1BF"];

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const displayPercent = formatToPercentString(percent);

  return (
    <g style={{ pointerEvents: "none" }}>
      <circle cx={x} cy={y} r={16} fill="#ECEAF8" strokeWidth={1} />
      <text
        x={x}
        y={y}
        fill="#000000"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={700}
      >
        {displayPercent}
      </text>
    </g>
  );
};

type DetailItem = {
  label: string;
  value: number;
  total_item_count: number;
  total_sales_amount: number;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="text-xs font-semibold text-gray-700 mb-1">{data.label}</p>
        <p className="text-xs text-gray-600">
          아이템 수: <span className="font-semibold">{data.total_item_count.toLocaleString()}</span>
        </p>
        <p className="text-xs text-gray-600">
          금액: <span className="font-semibold">{formatKoreanCurrencyWithWon(data.total_sales_amount)}</span>
        </p>
      </div>
    );
  }
  return null;
};

type CompanyData = {
  company_name: string;
  total_amount: number;
  margin_rate: number;
  details: DetailItem[];
};

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardPurchaseTop5({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<CompanyData[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = getDashboardProjectFilterToString(filter);
      const result = await GetTopBuyersByScenario(params);

      if (result.data.success) {
        const newData: CompanyData[] = result.data.pods[0].content.items.map(
          (item) => ({
            company_name: item.buyer,
            total_amount: item.total_sales_amount,
            margin_rate: item.margin_rate,
            details: item.products.map((product) => ({
              label: product.product_name,
              value: product.total_sales_amount,
              total_item_count: product.total_item_count,
              total_sales_amount: product.total_sales_amount,
            })),
          })
        );
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleExcelDownload = () => {
    if (data.length === 0) {
      showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
      return;
    }

    const columns = [
      { header: "순위", key: "rank", width: 10 },
      { header: "업체 이름", key: "company_name", width: 30 },
      { header: "총 매입가", key: "total_amount", width: 20 },
      { header: "마진율", key: "margin_rate", width: 15 },
    ];

    const excelData = data.map((item, index) => ({
      rank: index + 1,
      company_name: item.company_name,
      total_amount: item.total_amount,
      margin_rate: formatToPercentString(item.margin_rate, 2),
    }));

    downloadExcel(
      excelData,
      columns,
      getExcelFileName("구매업체별_Top5_매입가")
    );
  };

  return (
    <DashboardCardHeader
      title={title}
      isLoading={isLoading}
      rightElement={
        <button
          className="flex items-center gap-1 text-xs text-gray-A hover:text-gray-600"
          onClick={handleExcelDownload}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.66666 6.66669L7.99999 10L11.3333 6.66669"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 10V2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          EXCEL 다운로드
        </button>
      }
    >
      <div className="-mx-7 -mb-5">
        <table className="w-full text-sm">
          <thead className="bg-dark-red-60 text-white text-left text-sm font-semibold sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 w-10"></th>
              <th className="px-4 py-2">업체 이름</th>
              <th className="px-4 py-2 text-right">총 매입가</th>
              <th className="px-4 py-2 text-right">마진율</th>
              <th className="px-4 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {data.map((company, index) => (
              <>
                <tr
                  key={index}
                  className="hover:bg-gray-E cursor-pointer border-b"
                  onClick={() => toggleExpand(index)}
                >
                  <td className="px-4 py-2 text-xs text-gray-A text-center">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-A">
                    {company.company_name}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-gray-A">
                    {formatKoreanCurrencyWithWon(company.total_amount)}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-gray-A">
                    {formatToPercentString(company.margin_rate, 2)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-transform ${
                        expandedIndex === index ? "rotate-90" : ""
                      }`}
                    >
                      <path
                        d="M6 12L10 8L6 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </td>
                </tr>
                {expandedIndex === index && (
                  <tr>
                    <td colSpan={5} className="p-4 bg-white">
                      <div className="flex">
                        <div
                          className="w-[30%]"
                          style={{
                            borderRight: "1px solid var(--Gray_11, #EEE)",
                          }}
                        >
                          <div
                            className="pb-2 mr-4"
                            style={{
                              color: "var(--Gray_7, #999)",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderBottom: "1px solid var(--Gray_8, #BBB)",
                            }}
                          >
                            구매 물품 세부내역
                          </div>
                          {company.details.map((item, detailIndex) => (
                            <div
                              key={detailIndex}
                              className="py-2 mr-4"
                              style={{
                                borderBottom: "1px solid var(--Gray_11, #EEE)",
                              }}
                            >
                              <span className="text-xs text-gray-A">
                                {item.label}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="w-[70%] flex items-center justify-center gap-4">
                          <PieChart width={180} height={180}>
                            <Tooltip
                              content={<CustomTooltip />}
                              wrapperStyle={{ zIndex: 100 }}
                            />
                            <Pie
                              data={company.details}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              labelLine={false}
                              label={renderCustomLabel}
                              dataKey="value"
                              minAngle={15}
                              paddingAngle={2}
                              isAnimationActive={false}
                            >
                              {company.details.map((entry, detailIndex) => (
                                <Cell
                                  key={`cell-${detailIndex}`}
                                  fill={COLORS[detailIndex % COLORS.length]}
                                />
                              ))}
                            </Pie>
                          </PieChart>
                          <div className="flex flex-col gap-2 w-[80px]">
                            {company.details.map((item, detailIndex) => (
                              <div
                                key={detailIndex}
                                className="flex items-center gap-2 text-xs"
                              >
                                <span
                                  className="w-3 h-3 min-w-[12px] rounded-full inline-block flex-shrink-0"
                                  style={{
                                    backgroundColor:
                                      COLORS[detailIndex % COLORS.length],
                                  }}
                                />
                                <span className="text-gray-A font-semibold truncate">
                                  {item.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardCardHeader>
  );
}
